/**
 * Shared pricing utilities for consistent price handling across the application
 * Optimized for performance and enhanced error handling
 */

/**
 * Format price with proper validation and error handling
 * @param {number|string} price - The price value to format
 * @returns {string} Formatted price string (e.g., "123.45")
 */
export const formatPrice = (price) => {
  const numPrice = parseFloat(price);
  return (isNaN(numPrice) || numPrice < 0) ? "0.00" : numPrice.toFixed(2);
};

/**
 * Calculate price per unit with enhanced validation
 * @param {string} bottleSize - The bottle size string (e.g., "100ml", "50 tablets")
 * @param {number|string} price - The price value
 * @returns {string} Formatted price per unit string (e.g., "$ 1.234") or empty string if invalid
 */
export const getPricePerUnit = (bottleSize, price) => {
  if (!bottleSize) return "";
  
  const numPrice = parseFloat(price);
  if (isNaN(numPrice) || numPrice <= 0) return "";

  const sizeMatch = bottleSize.match(/[0-9.,]+/g);
  if (!sizeMatch || sizeMatch.length !== 1) return "";

  const size = parseFloat(sizeMatch[0]);
  if (isNaN(size) || size <= 0) return "";

  const ppu = Math.round((numPrice / size) * 1000) / 1000;
  return isFinite(ppu) ? `$ ${ppu.toFixed(3)}` : "";
};

/**
 * Validate if a price value is valid
 * @param {number|string} price - The price value to validate
 * @returns {boolean} True if price is valid, false otherwise
 */
export const isValidPrice = (price) => {
  const numPrice = parseFloat(price);
  return !isNaN(numPrice) && numPrice >= 0 && isFinite(numPrice);
};

/**
 * Apply custom pricing to a product object
 * @param {Object} product - The product object
 * @param {number|string} customPrice - The custom price to apply
 * @returns {Object} Product with updated pricing
 */
export const applyCustomPrice = (product, customPrice) => {
  if (!isValidPrice(customPrice)) return product;
  
  return {
    ...product,
    price: parseFloat(customPrice),
    hasCustomPrice: true,
    basePrice: product.basePrice || product.price
  };
};

/**
 * Get the best price from multiple pricing options
 * @param {Array} prices - Array of price values
 * @returns {number} The lowest valid price
 */
export const getBestPrice = (prices) => {
  const validPrices = prices
    .map(price => parseFloat(price))
    .filter(price => isValidPrice(price));
  
  return validPrices.length > 0 ? Math.min(...validPrices) : 0;
};

/**
 * Format price for display with currency symbol
 * @param {number|string} price - The price value
 * @param {string} currency - Currency symbol (default: "$")
 * @returns {string} Formatted price with currency (e.g., "$ 123.45")
 */
export const formatPriceWithCurrency = (price, currency = "$") => {
  const formattedPrice = formatPrice(price);
  return `${currency} ${formattedPrice}`;
};

/**
 * Calculate discount percentage between two prices
 * @param {number|string} originalPrice - Original price
 * @param {number|string} discountedPrice - Discounted price
 * @returns {number} Discount percentage (0-100)
 */
export const calculateDiscountPercentage = (originalPrice, discountedPrice) => {
  const original = parseFloat(originalPrice);
  const discounted = parseFloat(discountedPrice);
  
  if (!isValidPrice(original) || !isValidPrice(discounted) || original <= 0) {
    return 0;
  }
  
  const discount = ((original - discounted) / original) * 100;
  return Math.max(0, Math.round(discount * 100) / 100); // Round to 2 decimal places
};
